import React, { useEffect, useState } from "react";
import KPITiles from "@/components/KPITiles";
import StatsScopeToggle from "@/components/StatsScopeToggle";
import OrdersTable from "@/components/OrdersTable";
import BulkImportButton from "@/components/BulkImportButton";
import api, { formatApiError } from "@/lib/api";
import useOrderEvents from "@/hooks/useOrderEvents";
import { Card } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { Search, PackagePlus, FileDown, Printer, AlertTriangle, CheckCircle2 } from "lucide-react";
import { Link, useSearchParams, useLocation } from "react-router-dom";
import { useAuth } from "@/context/AuthContext";
import { toast } from "sonner";
import { downloadOrdersCsv } from "@/lib/exportOrdersCsv";
import { printBulkLabels } from "@/lib/printBulkLabels";
import { makeScopeFilter } from "@/lib/scope";

const SCOPE_LABEL = { week: "This Week", month: "This Month" };

// Map URL ?status= shortcut → local filter dropdown value
const STATUS_QUERY_MAP = {
  on_route: "assigned",
  unassigned: "unassigned",
  accepted: "accepted",
  assigned: "assigned",
  in_transit: "in_transit",
  delivered: "delivered",
  failed: "failed",
  returned: "returned",
  all: "all",
};

export default function AdminOrders() {
  const { user } = useAuth();
  const location = useLocation();
  const isSuperAdmin = user?.role === "super_admin";
  const newOrderHref = isSuperAdmin ? "/admin/orders/new" : "/dispatch/orders/new";
  const [searchParams, setSearchParams] = useSearchParams();
  const initialStatus = STATUS_QUERY_MAP[searchParams.get("status") || ""] || "all";
  const [stats, setStats] = useState({});
  // Seed with an optimistic row if we just came back from "New order" so Miles / Price
  // / Pharmacy stay visible while the /orders refetch is in flight.
  const [orders, setOrders] = useState(() => (location.state?.newOrder ? [location.state.newOrder] : []));
  const [pharmacies, setPharmacies] = useState([]);
  const [drivers, setDrivers] = useState([]);
  const [filterStatus, setFilterStatus] = useState(initialStatus);
  const [filterPh, setFilterPh] = useState("all");
  const [filterDeliverType, setFilterDeliverType] = useState("all");
  const [filterDriver, setFilterDriver] = useState("all");
  const [filterCopay, setFilterCopay] = useState(null); // null | "outstanding" | "collected"
  const [search, setSearch] = useState("");
  // Debounce search keystrokes → backend query. Needed because the endpoint
  // returns the newest 1000 orders; older bulk-imported orders won't be in
  // the client list, so a pure client-side .filter() would never find them.
  const [debouncedSearch, setDebouncedSearch] = useState("");
  useEffect(() => {
    const id = setTimeout(() => setDebouncedSearch(search.trim()), 300);
    return () => clearTimeout(id);
  }, [search]);
  const [scope, setScope] = useState("week"); // "month" | "week"

  // One-shot hand-off — clear so a filter change / refresh doesn't re-inject the row.
  useEffect(() => {
    if (location.state?.newOrder) {
      window.history.replaceState({}, "");
    }
  }, []); // eslint-disable-line react-hooks/exhaustive-deps

  // When the URL changes (clicked a different KPI tile from Overview), refresh the local filter
  useEffect(() => {
    const next = STATUS_QUERY_MAP[searchParams.get("status") || ""] || "all";
    setFilterStatus(next);
    const cop = searchParams.get("copay");
    setFilterCopay(cop === "outstanding" || cop === "collected" ? cop : null);
  }, [searchParams]);

  const load = () => {
    const params = {};
    if (filterCopay) {
      params.copay = filterCopay;
    } else if (filterStatus === "unassigned") params.unassigned = true;
    else if (filterStatus === "accepted") params.accepted = true;
    else if (filterStatus !== "all") params.status = filterStatus;
    if (filterPh !== "all") params.pharmacy_id = filterPh;
    if (debouncedSearch) params.search = debouncedSearch;
    Promise.all([
      api.get("/stats/overview", { params: { scope } }),
      api.get("/orders", { params }),
    ]).then(([s, o]) => { setStats(s.data); setOrders(o.data); });
  };

  useEffect(() => {
    api.get("/pharmacies").then((r) => setPharmacies(r.data));
    api.get("/users", { params: { role: "driver" } }).then((r) => setDrivers(r.data));
  }, []);
  useEffect(() => { load(); }, [filterStatus, filterPh, scope, filterCopay, debouncedSearch]);
  useEffect(() => {
    const id = setInterval(load, 10000);
    return () => clearInterval(id);
  }, [filterStatus, filterPh, scope, filterCopay, debouncedSearch]);

  // Real-time push: the moment a driver marks any order Delivered / Failed in
  // the caller's workspace, merge the new fields into local state so the row's
  // status pill flips (and the KPI counts refresh on the next 10-second poll).
  // We refetch stats piggy-back on the existing poll to keep KPI tiles honest.
  useOrderEvents((update) => {
    setOrders((prev) => {
      const idx = prev.findIndex((o) => o.id === update.id);
      if (idx < 0) return prev;
      const next = prev.slice();
      next[idx] = { ...prev[idx], ...update };
      return next;
    });
  });

  const filtered = orders.filter(makeScopeFilter(scope)).filter((o) => {
    if (filterDeliverType !== "all" && (o.deliver_type || "") !== filterDeliverType) return false;
    if (filterDriver !== "all") {
      if (filterDriver === "none" ? o.driver_id : o.driver_id !== filterDriver) return false;
    }
    if (!search.trim()) return true;
    const q = search.toLowerCase();
    return (
      o.customer_name?.toLowerCase().includes(q) ||
      o.address?.toLowerCase().includes(q) ||
      o.customer_phone?.toLowerCase().includes(q) ||
      o.driver_name?.toLowerCase().includes(q) ||
      o.pharmacy_name?.toLowerCase().includes(q) ||
      o.order_number?.toLowerCase().includes(q)
    );
  });

  const onDelete = async (o) => {
    if (!confirm(`Delete order for ${o.customer_name}?`)) return;
    try { await api.delete(`/orders/${o.id}`); toast.success("Order deleted"); load(); }
    catch (e) { toast.error("Could not delete order"); }
  };

  const onExportExcel = () => {
    if (!filtered.length) {
      toast.error("No orders to export with these filters.");
      return;
    }
    const basename = isSuperAdmin ? "admin-orders" : "dispatch-orders";
    const { count } = downloadOrdersCsv(filtered, basename, scope);
    toast.success(`Exported ${count} order${count === 1 ? "" : "s"} (${SCOPE_LABEL[scope]})`);
  };

  const [printing, setPrinting] = useState(false);
  const [accepting, setAccepting] = useState(false);
  const [selected, setSelected] = useState(() => new Set());
  const [milesAudit, setMilesAudit] = useState(null); // null | { pharmacies_missing_coords, orders_missing_coords }
  const [repairing, setRepairing] = useState(false);

  // Super-admin-only: auto-check whether any orders are missing the coordinates
  // needed to compute Miles. If so, surface a one-click "Repair Miles" banner.
  useEffect(() => {
    if (!isSuperAdmin) return;
    api.get("/admin/miles/audit").then((r) => setMilesAudit(r.data)).catch(() => {});
  }, [isSuperAdmin]);

  const onRepairMiles = async () => {
    setRepairing(true);
    try {
      const { data } = await api.post("/admin/miles/repair");
      const okCount = (data.fixed_pharmacies || 0) + (data.fixed_orders || 0);
      const failCount = (data.failed_pharmacies || 0) + (data.failed_orders || 0);
      if (failCount === 0) {
        toast.success(`Repaired ${okCount} record${okCount === 1 ? "" : "s"} — Miles will now show on every order`);
      } else {
        toast.warning(`Repaired ${okCount}, but ${failCount} could not be geocoded (check addresses)`);
      }
      setMilesAudit(null);
      load();
    } catch (e) {
      toast.error(formatApiError(e));
    } finally {
      setRepairing(false);
    }
  };

  useEffect(() => {
    setSelected((prev) => {
      const visibleIds = new Set(filtered.map((o) => o.id));
      const next = new Set();
      prev.forEach((id) => { if (visibleIds.has(id)) next.add(id); });
      return next.size === prev.size ? prev : next;
    });
  }, [filtered]);

  const toggleSelected = (id) => setSelected((prev) => {
    const next = new Set(prev);
    if (next.has(id)) next.delete(id); else next.add(id);
    return next;
  });
  const toggleSelectAll = () => setSelected((prev) => (
    prev.size >= filtered.length ? new Set() : new Set(filtered.map((o) => o.id))
  ));

  const onPrintBulkLabels = async () => {
    const target = selected.size > 0
      ? filtered.filter((o) => selected.has(o.id))
      : filtered;
    if (!target.length) {
      toast.error("No orders to print.");
      return;
    }
    setPrinting(true);
    try {
      const result = await printBulkLabels(target);
      const what = selected.size > 0 ? "selected" : SCOPE_LABEL[scope];
      toast.success(`Printing ${result.count} label${result.count === 1 ? "" : "s"} (${what})`);
    } catch (e) {
      toast.error(`Could not generate labels: ${e.message || e}`);
    } finally {
      setPrinting(false);
    }
  };

  // Pool of orders that are still eligible for "Accept" (not yet received from
  // pharmacy and not in a closed state). Used to decide whether to enable the
  // bulk accept button and what subset to send.
  const acceptablePool = (selected.size > 0 ? filtered.filter((o) => selected.has(o.id)) : filtered)
    .filter((o) => !o.received_from_pharmacy_at && !["delivered", "failed", "returned"].includes(o.status));

  const onBulkAccept = async () => {
    if (!acceptablePool.length) {
      toast.error("No orders eligible for Accept (already accepted or closed).");
      return;
    }
    const noun = `${acceptablePool.length} order${acceptablePool.length === 1 ? "" : "s"}`;
    if (!confirm(`Mark ${noun} as received from pharmacy?`)) return;
    setAccepting(true);
    try {
      const { data } = await api.post("/orders/bulk-intake", { order_ids: acceptablePool.map((o) => o.id) });
      const parts = [`Accepted ${data.accepted_count} order${data.accepted_count === 1 ? "" : "s"}`];
      if (data.already_count) parts.push(`${data.already_count} already accepted`);
      if (data.forbidden_count) parts.push(`${data.forbidden_count} not allowed`);
      if (data.not_found_count) parts.push(`${data.not_found_count} not found`);
      toast.success(parts.join(" · "));
      setSelected(new Set());
      load();
    } catch (e) {
      toast.error(formatApiError(e));
    } finally {
      setAccepting(false);
    }
  };

  return (
    <div className="p-4 lg:p-8 max-w-[1800px] mx-auto" data-testid="admin-orders-page">
      <header className="mb-6 flex items-end justify-between gap-4 flex-wrap">
        <div>
          <h1 className="text-3xl lg:text-4xl font-display font-bold tracking-tight">Orders</h1>
          <p className="text-muted-foreground mt-1.5">Every delivery order across the platform.</p>
        </div>
        <div className="flex items-center gap-2">
          <Button
            variant="outline"
            className="rounded-full"
            onClick={onBulkAccept}
            disabled={accepting || !acceptablePool.length}
            data-testid="bulk-accept-orders-btn"
            title={selected.size > 0
              ? `Accept ${acceptablePool.length} selected order${acceptablePool.length === 1 ? "" : "s"}`
              : `Accept all eligible filtered orders (${SCOPE_LABEL[scope]})`}
          >
            <CheckCircle2 className="h-4 w-4 mr-1.5" />
            {accepting
              ? "Accepting…"
              : selected.size > 0
                ? `Accept ${acceptablePool.length}`
                : (acceptablePool.length > 0 ? `Accept ${acceptablePool.length}` : "Accept")}
          </Button>
          <Button
            variant="outline"
            className="rounded-full"
            onClick={onPrintBulkLabels}
            disabled={printing}
            data-testid="bulk-print-labels-btn"
            title={`Print labels for all filtered orders (${SCOPE_LABEL[scope]})`}
          >
            <Printer className="h-4 w-4 mr-1.5" /> {printing ? "Printing…" : (selected.size > 0 ? `Print ${selected.size} label${selected.size === 1 ? "" : "s"}` : "Print labels")}
          </Button>
          <Button
            variant="outline"
            className="rounded-full"
            onClick={onExportExcel}
            data-testid="admin-export-excel-btn"
            title={`Export ${SCOPE_LABEL[scope]} orders to Excel-compatible CSV`}
          >
            <FileDown className="h-4 w-4 mr-1.5" /> Export · {SCOPE_LABEL[scope]}
          </Button>
          <Link to={newOrderHref}>
            <Button className="rounded-full" data-testid="admin-new-order-btn">
              <PackagePlus className="h-4 w-4 mr-1.5" /> New order
            </Button>
          </Link>
          {isSuperAdmin && <BulkImportButton onImported={load} />}
        </div>
      </header>

      {/* Miles repair banner (super_admin only) — surfaces missing lat/lng so the
          Miles column never quietly shows "—". One click re-geocodes & flushes
          the distance cache so the next refresh fills every Miles cell. */}
      {isSuperAdmin && milesAudit && (milesAudit.pharmacies_missing_coords > 0 || milesAudit.orders_missing_coords > 0) && (
        <div
          className="mb-4 rounded-xl border border-amber-300/60 bg-amber-50 dark:bg-amber-950/30 dark:border-amber-700/40 px-4 py-3 flex items-center gap-3"
          data-testid="miles-repair-banner"
        >
          <AlertTriangle className="h-5 w-5 text-amber-600 dark:text-amber-400 shrink-0" />
          <div className="flex-1 text-sm">
            <span className="font-medium">Miles not showing on some orders.</span>
            {" "}
            {milesAudit.pharmacies_missing_coords > 0 && (<>{milesAudit.pharmacies_missing_coords} pharmac{milesAudit.pharmacies_missing_coords === 1 ? "y is" : "ies are"} missing geo-coordinates. </>)}
            {milesAudit.orders_missing_coords > 0 && (<>{milesAudit.orders_missing_coords} order{milesAudit.orders_missing_coords === 1 ? " is" : "s are"} missing geo-coordinates.</>)}
          </div>
          <Button
            size="sm"
            onClick={onRepairMiles}
            disabled={repairing}
            className="rounded-full"
            data-testid="miles-repair-btn"
          >
            {repairing ? "Repairing…" : "Repair miles"}
          </Button>
        </div>
      )}

      <section className="mb-6">
        <div className="flex items-center justify-between mb-3">
          <h2 className="font-display font-semibold text-base text-muted-foreground uppercase tracking-wide">Resume of day</h2>
          <StatsScopeToggle value={scope} onChange={setScope} />
        </div>
        <KPITiles
          items={[
            { label: "Unassigned", value: stats.out_of_route, hint: "Out of route", color: "yellow", onClick: () => { setFilterCopay(null); setFilterStatus("unassigned"); setSearchParams({status:"unassigned"}); } },
            {
              label: "Accepted",
              value: stats.received_from_pharmacy,
              hint: stats.pending_intake ? `${stats.pending_intake} pending intake` : "from pharmacy",
              color: "lightBlue",
              onClick: () => { setFilterCopay(null); setFilterStatus("accepted"); setSearchParams({status:"accepted"}); },
            },
            { label: "On Route", value: stats.routed, hint: "Orders on a route", color: "sky", onClick: () => { setFilterCopay(null); setFilterStatus("assigned"); setSearchParams({status:"on_route"}); } },
            { label: "Delivered", value: stats.delivered, hint: "Completed", color: "green", onClick: () => { setFilterCopay(null); setFilterStatus("delivered"); setSearchParams({status:"delivered"}); } },
            { label: "Failed", value: stats.failed, hint: "Could not deliver", color: "red", onClick: () => { setFilterCopay(null); setFilterStatus("failed"); setSearchParams({status:"failed"}); } },
            { label: "Returned", value: stats.returned, hint: "Returned to pharmacy", color: "orange", onClick: () => { setFilterCopay(null); setFilterStatus("returned"); setSearchParams({status:"returned"}); } },
            { label: "Total Open", value: stats.open_orders, hint: "All open (not delivered)", onClick: () => { setFilterCopay(null); setFilterStatus("all"); setSearchParams({}); } },
            {
              label: "Copays",
              value: stats.copay_collected_count ?? 0,
              hint: stats.copay_outstanding_count
                ? `${stats.copay_outstanding_count} outstanding · $${(stats.copay_outstanding_total ?? 0).toFixed(2)}`
                : `$${(stats.copay_collected_total ?? 0).toFixed(2)} collected`,
              color: "white",
              onClick: () => { setFilterStatus("all"); setFilterCopay("collected"); setSearchParams({copay:"collected"}); },
            },
          ]}
        />
      </section>

      {filterCopay && (
        <div className="mb-4 inline-flex items-center gap-2 rounded-full bg-[#e3f0e1] border border-[#7A9B7A]/50 px-3 py-1.5 text-sm" data-testid="copay-filter-banner">
          <span className="font-medium text-[#234123]">
            Showing {filterCopay === "outstanding" ? "uncollected copays" : "collected copays"}
          </span>
          {filterCopay === "collected" && (
            <button
              type="button"
              onClick={() => { setFilterCopay("outstanding"); setSearchParams({copay:"outstanding"}); }}
              className="ml-1 text-xs text-[#234123] underline hover:no-underline"
              data-testid="copay-filter-show-outstanding"
            >
              Show uncollected
            </button>
          )}
          {filterCopay === "outstanding" && (
            <button
              type="button"
              onClick={() => { setFilterCopay("collected"); setSearchParams({copay:"collected"}); }}
              className="ml-1 text-xs text-[#234123] underline hover:no-underline"
              data-testid="copay-filter-show-collected"
            >
              Show collected
            </button>
          )}
          <button
            type="button"
            onClick={() => { setFilterCopay(null); setSearchParams({}); }}
            className="ml-2 text-xs text-muted-foreground hover:text-foreground"
            data-testid="copay-filter-clear"
          >
            ✕ Clear
          </button>
        </div>
      )}

      <Card className="p-4 rounded-xl border-border bg-card/80">
        <div className="flex flex-col md:flex-row gap-3 items-stretch md:items-center flex-wrap">
          <div className="relative flex-1 min-w-[200px]">
            <Search className="absolute left-3 top-1/2 -translate-y-1/2 h-4 w-4 text-muted-foreground" />
            <Input placeholder="Search order #, customer, address, phone, driver…" className="pl-9" value={search} onChange={(e) => setSearch(e.target.value)} data-testid="orders-search" />
          </div>
          <div className="w-full md:w-40">
            <Select value={filterStatus} onValueChange={setFilterStatus}>
              <SelectTrigger data-testid="filter-status"><SelectValue /></SelectTrigger>
              <SelectContent>
                <SelectItem value="all">All statuses</SelectItem>
                <SelectItem value="unassigned">Unassigned</SelectItem>
                <SelectItem value="accepted">Accepted</SelectItem>
                <SelectItem value="assigned">On route</SelectItem>
                <SelectItem value="in_transit">In transit</SelectItem>
                <SelectItem value="delivered">Delivered</SelectItem>
                <SelectItem value="failed">Failed</SelectItem>
                <SelectItem value="returned">Returned</SelectItem>
              </SelectContent>
            </Select>
          </div>
          <div className="w-full md:w-40">
            <Select value={filterDeliverType} onValueChange={setFilterDeliverType}>
              <SelectTrigger data-testid="filter-deliver-type"><SelectValue /></SelectTrigger>
              <SelectContent>
                <SelectItem value="all"><span className="lg:hidden">All types</span><span className="hidden lg:inline">All delivery types</span></SelectItem>
                <SelectItem value="same_day">Same day</SelectItem>
                <SelectItem value="next_day">Next day</SelectItem>
                <SelectItem value="otc">OTC</SelectItem>
                <SelectItem value="pickup">Pick Up</SelectItem>
              </SelectContent>
            </Select>
          </div>
          <div className="w-full md:w-48">
            <Select value={filterDriver} onValueChange={setFilterDriver}>
              <SelectTrigger data-testid="filter-driver"><SelectValue /></SelectTrigger>
              <SelectContent>
                <SelectItem value="all">All drivers</SelectItem>
                <SelectItem value="none">— No driver —</SelectItem>
                {drivers.map((d) => <SelectItem key={d.id} value={d.id}>{d.name}</SelectItem>)}
              </SelectContent>
            </Select>
          </div>
          <div className="w-full md:w-44">
            <Select
              value={filterCopay || "all"}
              onValueChange={(v) => {
                if (v === "all") {
                  setFilterCopay(null);
                  const sp = Object.fromEntries(searchParams.entries());
                  delete sp.copay;
                  setSearchParams(sp);
                } else {
                  setFilterCopay(v);
                  setSearchParams({ copay: v });
                }
              }}
            >
              <SelectTrigger data-testid="filter-copay"><SelectValue placeholder="Copays" /></SelectTrigger>
              <SelectContent>
                <SelectItem value="all">All copays</SelectItem>
                <SelectItem value="outstanding">Uncollected</SelectItem>
                <SelectItem value="collected">Collected</SelectItem>
              </SelectContent>
            </Select>
          </div>
          <div className="w-full md:w-52">
            <Select value={filterPh} onValueChange={setFilterPh}>
              <SelectTrigger data-testid="filter-pharmacy"><SelectValue /></SelectTrigger>
              <SelectContent>
                <SelectItem value="all">All pharmacies</SelectItem>
                {pharmacies.map((p) => <SelectItem key={p.id} value={p.id}>{p.name}</SelectItem>)}
              </SelectContent>
            </Select>
          </div>
        </div>
      </Card>

      <div className="mt-4">
        <OrdersTable orders={filtered} showPharmacy showDriverAndRoute showCopay selectable selected={selected} onToggle={toggleSelected} onToggleAll={toggleSelectAll} onDelete={onDelete} onChanged={load} emptyMessage="No orders match these filters." />
        <div className="text-xs text-muted-foreground mt-2 font-mono-num">{filtered.length} of {orders.length} orders</div>
      </div>
    </div>
  );
}
